import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { X, Menu, Info, Mail, User } from "lucide-react";
import { MarketingScrollLockup } from "@/components/MarketingScrollSymbol";

const SERVICE_LINKS = [
  { label: "Service update", href: "/", icon: Info },
  { label: "Support", href: "/support", icon: Mail },
  { label: "Delete account", href: "/delete-account", icon: User },
];

/* ── Header ── */

export default function MarketingHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [location] = useLocation();

  // Close menu on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  // Lock body scroll when menu is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  // Blur header on scroll — guard against no-op updates
  useEffect(() => {
    function onScroll() {
      const next = window.scrollY > 40;
      setScrolled((prev) => (prev === next ? prev : next));
    }
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <div className={`marketing-header-shell fixed top-0 left-0 right-0 z-30 transition-all duration-300 ${scrolled ? "nav-header-blur" : ""}`}>
        <header className="relative max-w-7xl mx-auto px-6 py-4 md:py-5 flex items-center justify-between">
          {/* Logo */}
          <Link href="/" className="flex shrink-0 marketing-logo">
            <MarketingScrollLockup preset="nav" />
          </Link>

          {/* Center nav — desktop */}
          <nav aria-label="Primary" className="hidden lg:flex items-center gap-7 absolute left-1/2 -translate-x-1/2">
            {SERVICE_LINKS.map(({ label, href, icon: Icon }) => (
              <Link
                key={href}
                href={href}
                className="flex items-center gap-2 text-sm font-medium landing-text-secondary hover:landing-text-primary transition-colors"
              >
                <Icon size={18} className="shrink-0" /> {label}
              </Link>
            ))}
          </nav>

          {/* Right side — support */}
          <div className="hidden lg:flex items-center gap-6">
            <Link href="/support" className="nav-download-pill">Get help</Link>
          </div>

          {/* Mobile — persistent support + hamburger */}
          <div className="flex lg:hidden items-center gap-3">
            <Link href="/support" className="nav-download-pill nav-download-pill-sm">Get help</Link>
            <button onClick={() => setMenuOpen(true)} aria-label="Open menu">
              <Menu size={20} className="landing-text-primary" />
            </button>
          </div>
        </header>
      </div>

      {/* Mobile overlay */}
      <div
        className={`fixed inset-0 z-40 transition-opacity duration-300 lg:hidden ${
          menuOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        style={{ backgroundColor: "rgba(29,25,22,0.3)" }}
        onClick={() => setMenuOpen(false)}
      />

      {/* Mobile slideout */}
      <aside
        className={`fixed top-0 left-0 z-50 h-full w-[300px] flex flex-col transition-transform duration-300 ease-out lg:hidden ${
          menuOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        style={{ backgroundColor: "var(--landing-bg)" }}
      >
        <div className="flex items-center justify-between px-6 py-6">
          <Link href="/" className="flex" onClick={() => setMenuOpen(false)}>
            <MarketingScrollLockup preset="nav" />
          </Link>
          <button
            onClick={() => setMenuOpen(false)}
            className="landing-text-tertiary hover:landing-text-primary transition-colors p-1.5 rounded-lg"
            aria-label="Close menu"
          >
            <X size={20} />
          </button>
        </div>

        <nav aria-label="Mobile" className="flex-1 px-3 overflow-y-auto">
          {SERVICE_LINKS.map(({ label, href, icon: Icon }) => (
            <Link key={href} href={href} onClick={() => setMenuOpen(false)}>
              <span className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium cursor-pointer landing-sidebar-nav-item">
                <Icon size={20} className="shrink-0" /> {label}
              </span>
            </Link>
          ))}
        </nav>

        <div className="px-3 py-4">
          <div className="px-3 mt-3 text-xs landing-text-tertiary">
            <Link href="/terms" className="hover:underline" onClick={() => setMenuOpen(false)}>Terms</Link>
            {" · "}
            <Link href="/privacy" className="hover:underline" onClick={() => setMenuOpen(false)}>Privacy</Link>
            {" · "}
            <Link href="/support" className="hover:underline" onClick={() => setMenuOpen(false)}>Support</Link>
          </div>
          <div className="px-3 text-xs mt-1 landing-text-tertiary" style={{ opacity: 0.6 }}>
            &copy; 2026 Scroll
          </div>
        </div>
      </aside>
    </>
  );
}
